/**
 * O segundo fator da pessoa (0085), a porta de dados da mesa e do celular.
 *
 * Ativar e em dois passos: `POST .../ativacao` sorteia a chave e a devolve uma
 * vez; `POST .../confirmacao` recebe o primeiro codigo do aplicativo e
 * devolve os dez codigos de recuperacao — tambem uma vez. Trocar os codigos
 * pede um codigo; desativar pede a SENHA e um motivo desde a R06 (p.68,
 * migracao 0093). A conferencia e do servidor, dentro do banco.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { formatarInteiro } from '@/lib/formato';
import { comCorpo, pedir } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';

export interface SituacaoDoSegundoFator {
  readonly ativo: boolean;
  readonly ativadoEm: string | null;
  readonly codigosRestantes: number;
}

export interface ChaveDoSegundoFator {
  /** Base32, para digitar no aplicativo. */
  readonly chave: string;
  /** `otpauth://…`: o que um QR carregaria, e o que o celular abre direto no aplicativo. */
  readonly endereco: string;
  /** O nome da conta que o aplicativo vai mostrar (o e-mail). */
  readonly conta: string;
}

const CHAVE = ['sessao', 'segundo-fator'] as const;

export function useSegundoFator() {
  return useQuery({
    queryKey: CHAVE,
    queryFn: () => pedir<SituacaoDoSegundoFator>('/sessao/segundo-fator'),
  });
}

/** Comeca a ativacao. Nao grava nada que valha ainda: o retorno e a propria chave na tela. */
export function useIniciarSegundoFator() {
  return useMutation({
    meta: { silencioso: true },
    mutationFn: () => comCorpo<ChaveDoSegundoFator>('POST', '/sessao/segundo-fator/ativacao', {}),
  });
}

export function useConfirmarSegundoFator(conta: string) {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<{ codigos: readonly string[] }>((r) => ({
        titulo: 'Segundo fator ativado',
        alvo: { nome: conta },
        fatos: [`${formatarInteiro(r.codigos.length)} códigos de recuperação`],
        efeito: 'A entrada pela senha passa a pedir o código do aplicativo',
      })),
      silenciarFalha: true,
    },
    mutationFn: (codigo: string) => comCorpo<{ ativo: true; codigos: readonly string[] }>('POST', '/sessao/segundo-fator/confirmacao', { codigo }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

/**
 * Desativar pela senha e pelo motivo (R06, p.68; 0093). O motivo vai a trilha.
 */
export function useDesativarSegundoFatorPelaSenha(conta: string) {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<unknown, { senha: string; motivo: string }>((_r, v) => ({
        titulo: 'Segundo fator desativado',
        alvo: { nome: conta },
        fatos: [`Motivo: ${v.motivo}`],
        efeito: 'A entrada volta a pedir só a senha; os códigos de recuperação deixaram de valer',
      })),
      silenciarFalha: true,
    },
    mutationFn: (v: { senha: string; motivo: string }) =>
      comCorpo<{ ativo: false }>('POST', '/sessao/segundo-fator/desativacao', { senha: v.senha, motivo: v.motivo }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

/**
 * @deprecated Desde a R06 (0093) o servidor pede a senha e o motivo, e recusa
 * o codigo sozinho ("a senha não chegou"). Use `useDesativarSegundoFatorPelaSenha`.
 * Fica so ate o celular trocar de chamada.
 */
export function useDesativarSegundoFator(conta: string) {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe(() => ({
        titulo: 'Segundo fator desativado',
        alvo: { nome: conta },
        efeito: 'A entrada volta a pedir só a senha; os códigos de recuperação deixaram de valer',
      })),
      silenciarFalha: true,
    },
    mutationFn: (codigo: string) => comCorpo<{ ativo: false }>('POST', '/sessao/segundo-fator/desativacao', { codigo }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useTrocarCodigosDeRecuperacao(conta: string) {
  const cliente = useQueryClient();
  return useMutation({
    meta: {
      retorno: retornoDe<{ codigos: readonly string[] }>((r) => ({
        titulo: 'Códigos de recuperação trocados',
        alvo: { nome: conta },
        fatos: [`${formatarInteiro(r.codigos.length)} códigos novos`],
        efeito: 'Os códigos antigos deixaram de valer',
      })),
      silenciarFalha: true,
    },
    mutationFn: (codigo: string) => comCorpo<{ codigos: readonly string[] }>('POST', '/sessao/segundo-fator/codigos', { codigo }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

/** A chave em grupos de quatro, para ler e digitar. */
export function chaveEmGrupos(chave: string): string {
  return chave.replace(/(.{4})(?=.)/g, '$1 ');
}

/** Os codigos de recuperacao como texto para copiar, um por linha. */
export function codigosComoTexto(codigos: readonly string[]): string {
  return ['Códigos de recuperação do CT Frotas — cada um vale uma vez', ...codigos].join('\n');
}
